import { useState, useCallback, useRef } from "react";
import { Upload, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

interface DropZoneUploadProps {
  onFileSelected: (file: File) => Promise<void> | void;
  accept?: string;
  maxSizeMB?: number;
  uploading?: boolean;
  className?: string;
  label?: string;
  sublabel?: string;
  uploadingLabel?: string;
  disabled?: boolean;
  /** Validate file type, return error message or null */
  validateFile?: (file: File) => string | null;
}

export function DropZoneUpload({
  onFileSelected,
  accept = "image/*",
  maxSizeMB = 20,
  uploading = false,
  className,
  label = "Drop, paste, or click to upload",
  sublabel,
  uploadingLabel = "Uploading...",
  disabled = false,
  validateFile,
}: DropZoneUploadProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const processFile = useCallback(async (file: File) => {
    if (maxSizeMB && file.size > maxSizeMB * 1024 * 1024) {
      toast.error(`File exceeds ${maxSizeMB}MB limit`);
      return;
    }
    if (validateFile) {
      const err = validateFile(file);
      if (err) { toast.error(err); return; }
    }
    await onFileSelected(file);
  }, [onFileSelected, maxSizeMB, validateFile]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation(); setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation(); setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation(); setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  }, [processFile]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
    if (inputRef.current) inputRef.current.value = "";
  }, [processFile]);

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={cn(
        "relative border-2 border-dashed rounded-lg p-3 text-center transition-all cursor-pointer",
        isDragOver ? "border-primary bg-primary/5" : "border-border/60 hover:border-border",
        (uploading || disabled) && "pointer-events-none opacity-60",
        className,
      )}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        onChange={handleChange}
        disabled={uploading || disabled}
      />
      {uploading ? (
        <div className="flex flex-col items-center gap-1">
          <Loader2 className="w-4 h-4 animate-spin text-primary" />
          <span className="text-[11px] text-muted-foreground">{uploadingLabel}</span>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-1">
          <Upload className="w-4 h-4 text-muted-foreground/60" />
          <span className="text-[10px] text-muted-foreground">{label}</span>
          {sublabel && <span className="text-[10px] text-muted-foreground/50">{sublabel}</span>}
        </div>
      )}
    </div>
  );
}
